import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Chatbot.css";

const INITIAL_MESSAGES = [
  {
    sender: "bot",
    isIntro: true,
    text: "Hi! Ask me about Quinterra's services, technologies, company details, MVP approach, or contact information.",
  },
];

const QUICK_ACTIONS = [
  "What services do you offer?",
  "Tell me about mobile app development",
  "Which technologies do you use?",
  "How can I contact Quinterra?",
];

const FOLLOW_UP_QUESTIONS = [
  "What services do you offer?",
  "Tell me about mobile app development",
  "Which technologies do you use?",
  "How can I contact Quinterra?",
  "What is custom software development?",
  "Tell me about UX and UI services",
  "Where is Quinterra located?",
  "Do you help startups with MVPs?",
  "Do you provide maintenance and support?",
  "Can you redesign an existing website?",
  "Do you build e-commerce applications?",
  "What industries do you work with?",
  "Do you provide cloud deployment?",
  "How do you ensure quality in projects?",
  "Do you offer API development?",
  "Do you provide UI/UX audits?",
  "What makes Quinterra different?",
  "Do you offer website development?",
  "Can Quinterra build mobile and web products?",
  "Do you have product solutions?",
  "How can I get a quote?",
  "What is QShikshak?",
  "How can QShikshak help my school?",
  "What is HR Assist?",
  "How can HR Assist help my company?",
];

const RELATED_QUESTION_MAP = {
  services: [
    "Tell me about mobile app development",
    "What is custom software development?",
    "Do you provide maintenance and support?",
    "Do you build e-commerce applications?",
    "Tell me about UX and UI services",
    "How can I contact Quinterra?",
    "Do you offer website development?"
  ],
  mobile: [
    "Which technologies do you use?",
    "Do you provide maintenance and support?",
    "How do you ensure quality in projects?",
    "Do you help startups with MVPs?",
    "How can I contact Quinterra?",
    "Can Quinterra build mobile and web products?"
  ],
  custom: [
    "Which technologies do you use?",
    "Do you offer API development?",
    "Do you provide maintenance and support?",
    "How can I contact Quinterra?",
    "How can I get a quote?"
  ],
  website: [
    "Can you redesign an existing website?",
    "Do you build e-commerce applications?",
    "Do you provide cloud deployment?",
    "Tell me about UX and UI services",
    "How can I contact Quinterra?",
    "How can I get a quote?"
  ],
  uxui: [
    "Do you provide UI/UX audits?",
    "Can you redesign an existing website?",
    "Which technologies do you use?",
    "How can I contact Quinterra?",
    "Do you offer website development?"
  ],
  technologies: [
    "Do you provide cloud deployment?",
    "Do you offer API development?",
    "How can I contact Quinterra?",
    "Can Quinterra build mobile and web products?"
  ],
  contact: [
    "What industries do you work with?",
    "Where is Quinterra located?",
    "What makes Quinterra different?",
    "Do you help startups with MVPs?",
    "How can I get a quote?"
  ],
  about: [
    "What services do you offer?",
    "What industries do you work with?",
    "What makes Quinterra different?",
    "Where is Quinterra located?",
    "How can I contact Quinterra?",
    "Do you have product solutions?"
  ],
  products: [
    "What is QShikshak?",
    "How can QShikshak help my school?",
    "What is HR Assist?",
    "How can HR Assist help my company?",
    "How can I get a quote?"
  ],
};

const RULES = [
  {
    keywords: ["hello", "hi", "hey"],
    reply: {
      text: "Hello! I can guide you through the website and answer common questions about Quinterra.",
    },
  },
  {
    keywords: ["service", "services", "offer", "solutions"],
    reply: {
      text: "Quinterra provides mobile app development, custom software development, website design and development, UX and UI services, and MVP-focused product work.",
      action: {
        label: "Open Services Section",
        route: "/",
        state: { scrollTo: "services" },
      },
    },
  },
  {
    keywords: [
      "mobile",
      "android",
      "android apps",
      "ios",
      "ios apps",
      "hybrid",
      "hybrid apps",
      "app",
      "app support",
      "maintenance",
    ],
    reply: {
      text: "Quinterra builds Android, iOS, and hybrid mobile apps. The website also highlights feature prioritization, mobile-focused UI/UX design, qualified development, QA testing, release, and maintenance.",
      action: { label: "Open Mobile App Page", route: "/mobile-app" },
    },
  },
  {
    keywords: [
      "custom",
      "software",
      "desktop apps",
      "desktop",
      "data pipelines",
      "iot",
      "iot apps",
      "software support",
      "software support services",
      "erp",
      "crm",
      "enterprise",
      "department",
    ],
    reply: {
      text: "Custom software development includes enterprise systems, departmental software, business-specific solutions, customer self-service apps, and software support services.",
      action: { label: "Open Custom Software Page", route: "/custom" },
    },
  },
  {
    keywords: [
      "website",
      "web",
      "frontend",
      "backend",
      "ecommerce",
      "e-commerce",
      "static sits",
      "static sites",
      "cms",
      "dynamic",
      "cms dynamic",
      "digital marketing",
    ],
    reply: {
      text: "Quinterra builds websites and web applications with attention to architecture, frameworks, cloud hosting, infrastructure, scalability, and search capabilities.",
      action: { label: "Open Website Page", route: "/website" },
    },
  },
  {
    keywords: [
      "ux",
      "ui",
      "design",
      "prototype",
      "prototyping",
      "branding",
      "visual design",
      "information architecture",
    ],
    reply: {
      text: "The UX and UI services include UX design, UI design, prototyping, information architecture, branding, and experience-focused product design for web and mobile interfaces.",
      action: { label: "Open UX/UI Services Page", route: "/ux-services" },
    },
  },
  {
    keywords: ["technology", "technologies", "stack", "react", "node", "python", "java", "aws", "azure", "javascript", "react native"],
    reply: {
      text: "The Technologies page highlights React.js, React Native, Node.js, Python, JavaScript, Java, AWS, and Azure.",
      action: { label: "Open Technologies Page", route: "/technologies" },
    },
  },
  {
    keywords: [
      "mvp",
      "mvps",
      "startup",
      "startups",
      "do you help startups with mvps",
      "idea",
      "validate",
      "goal",
    ],
    reply: {
      text: "Yes, absolutely. We support startups, growing businesses, and established organizations that need websites, mobile apps, custom software, or MVP development. If you have an idea and want to turn it into a practical digital product, Quinterra can help you plan, build, and launch it with the right quality and direction.",
      action: { label: "Open About Page", route: "/about" },
    },
  },
  {
    keywords: [
      "contact",
      "contact quinterra",
      "contact you",
      "how can i contact",
      "email",
      "phone",
      "reach",
      "message",
      "enquiry",
    ],
    reply: {
      text: "You can contact Quinterra through the About page contact form. The site says enquiries are answered within 24 hours on business days. Email: info@quinterrasoft.com. Phone: 7780769388.",
      action: {
        label: "Open Contact Page",
        route: "/about",
        state: { scrollTo: "contact" },
      },
    },
  },
  {
    keywords: [
      "address",
      "location",
      "where",
      "where is quinterra located",
      "where are you located",
      "hyderabad",
      "telangana",
    ],
    reply: {
      text: "Quinterra Software Solutions Pvt. Ltd is listed at Bangaru Building, Laxmareddy Palam, Hyderabad, Telangana.",
    },
  },
  {
    keywords: ["about", "company", "quinterra", "who are you", "mission"],
    reply: {
      text: "Quinterra Soft presents itself as a product-focused software company built around creativity, strong partnerships, transparency, quality, and long-term client success.",
      action: { label: "Open About Page", route: "/about" },
    },
  },
  {
    keywords: ["price", "pricing", "cost", "quote", "budget"],
    reply: {
      text: "Pricing is not listed on the website because it depends on scope, feature set, and delivery timeline. The best next step is to contact the team for a quote.",
      action: {
        label: "Request a Quote",
        route: "/about",
        state: { scrollTo: "contact" },
      },
    },
  },
  {
    keywords: ["time", "timeline", "duration", "deadline", "delivery"],
    reply: {
      text: "The website does not list fixed delivery times. Timelines depend on complexity, and Quinterra's process starts with project examination and feature prioritization.",
    },
  },
  {
    keywords: ["maintenance", "support", "after delivery", "updates", "bug fix"],
    reply: {
      text: "Yes, Quinterra provides ongoing maintenance and support after project delivery, including updates, bug fixes, performance improvements, and feature enhancements.",
    },
  },
  {
    keywords: ["redesign", "improve website", "revamp", "upgrade website"],
    reply: {
      text: "Yes, Quinterra can redesign and improve your existing website with modern UI/UX, better performance, and updated technologies.",
    },
  },
  {
    keywords: ["ecommerce", "e-commerce", "online store", "shopping website"],
    reply: {
      text: "Quinterra builds scalable e-commerce platforms with secure payments, product management, user-friendly design, and performance optimization.",
    },
  },
  {
    keywords: ["industries", "domains", "sectors", "who do you work for"],
    reply: {
      text: "Quinterra works with startups, small businesses, and enterprises across multiple industries including education, media, business services, and digital platforms.",
    },
  },
  {
    keywords: ["cloud", "deployment", "hosting", "aws", "azure", "server"],
    reply: {
      text: "Yes, Quinterra provides cloud deployment and hosting solutions using platforms like AWS and Azure, ensuring scalability, security, and reliability.",
    },
  },
  {
    keywords: ["quality", "testing", "qa", "bug", "performance"],
    reply: {
      text: "Quinterra follows a structured QA process including testing, performance checks, and continuous monitoring to ensure high-quality software delivery.",
    },
  },
  {
    keywords: ["api", "integration api", "backend api", "rest api"],
    reply: {
      text: "Yes, Quinterra develops secure and scalable APIs for web and mobile applications, enabling seamless integration between systems.",
    },
  },
  {
    keywords: ["audit", "ui audit", "ux audit", "design review"],
    reply: {
      text: "Yes, Quinterra provides UI/UX audits to evaluate usability, improve user experience, and optimize product design.",
    },
  },
  {
    keywords: ["why quinterra", "difference", "why choose you", "unique", "special"],
    reply: {
      text: "Quinterra stands out with a product-focused approach, strong client collaboration, modern technologies, scalable solutions, and commitment to quality and innovation.",
    },
  },
  {
    keywords: ["offer", "flexible", "creative", "innovative", "design"],
    reply: {
      text: "The Home page highlights four qualities in Quinterra's approach: flexible, creative, design-focused, and innovative software solutions.",
    },
  },
  {
    keywords: ["product", "products", "ai assist", "qshikshak", "qmedia"],
    reply: {
      text: "The navigation mentions products such as AI Assist, Qshikshak, and Qmedia as part of Quinterra's broader product ecosystem.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["what is qshikshak", "qshikshak", "school management", "school system"],
    reply: {
      text: "QShikshak is a complete school management system designed to handle daily academic and administrative activities. It helps schools manage everything in one platform instead of using multiple systems.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["how can qshikshak help my school", "qshikshak help", "school automation", "help my school"],
    reply: {
      text: "QShikshak reduces manual work by automating tasks like attendance, student records, exams, and fee management. It improves efficiency and helps schools run smoothly with better organization.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["what features does qshikshak provide", "qshikshak features", "qshikshak provide", "school features"],
    reply: {
      text: "QShikshak offers key features such as student management, attendance tracking, exam and result handling, fee management, and communication tools for schools and parents.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["attendance and student records", "manage attendance", "student records", "qshikshak attendance"],
    reply: {
      text: "Yes, QShikshak allows schools to easily record daily attendance and maintain detailed student profiles. All data is stored securely and can be accessed anytime when needed.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["is qshikshak easy to use", "qshikshak easy", "easy to use qshikshak"],
    reply: {
      text: "Yes, it is designed with a simple and user-friendly interface so that teachers, staff, and administrators can use it without technical knowledge.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["demo of qshikshak", "qshikshak demo", "can i get a demo of qshikshak"],
    reply: {
      text: "Yes, you can request a demo to understand how QShikshak works and how it can benefit your school before making a decision.",
      action: { label: "Open Contact Page", route: "/about", state: { scrollTo: "contact" } },
    },
  },
  {
    keywords: ["what is hr assist", "hr assist", "hr software", "human resource management"],
    reply: {
      text: "HR Assist is a human resource management software that helps organizations manage employee-related activities efficiently. It brings all HR operations into one centralized system.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["how can hr assist help my company", "hr assist help", "help my company", "hr operations"],
    reply: {
      text: "HR Assist simplifies HR tasks by automating processes like employee management, attendance tracking, payroll, and recruitment. This saves time and improves overall productivity.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["what features does hr assist offer", "hr assist features", "hr assist offer", "payroll features"],
    reply: {
      text: "HR Assist includes features such as employee data management, attendance and leave tracking, payroll processing, recruitment support, and reporting tools for better decision-making.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["manage employees and payroll", "employees and payroll", "hr assist payroll", "employee records"],
    reply: {
      text: "Yes, HR Assist helps maintain employee records and automates payroll calculations, making salary processing accurate and hassle-free.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["is hr assist easy to use", "hr assist easy", "easy to use hr assist"],
    reply: {
      text: "Yes, it is designed to be simple and intuitive, making it easy for HR teams and employees to use without much training.",
      action: { label: "Open Products Page", route: "/products" },
    },
  },
  {
    keywords: ["demo of hr assist", "hr assist demo", "can i request a demo of hr assist"],
    reply: {
      text: "Yes, you can request a demo to explore its features and understand how it can improve your HR operations.",
      action: { label: "Open Contact Page", route: "/about", state: { scrollTo: "contact" } },
    },
  },
];

function normalizeText(text) {
  return text.toLowerCase().replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
}

function getKeywordScore(normalizedMessage, messageTokens, keyword) {
  const normalizedKeyword = normalizeText(keyword);

  if (!normalizedKeyword) {
    return 0;
  }

  if (normalizedKeyword.includes(" ")) {
    return normalizedMessage.includes(normalizedKeyword)
      ? normalizedKeyword.split(" ").length + 1
      : 0;
  }

  return messageTokens.has(normalizedKeyword) ? 1 : 0;
}

function getReply(message) {
  const normalizedMessage = normalizeText(message);
  const messageTokens = new Set(normalizedMessage.split(" ").filter(Boolean));

  const matchedRule = RULES.reduce(
    (bestMatch, rule) => {
      const score = rule.keywords.reduce(
        (totalScore, keyword) =>
          totalScore + getKeywordScore(normalizedMessage, messageTokens, keyword),
        0
      );

      if (score > bestMatch.score) {
        return { rule, score };
      }

      return bestMatch;
    },
    { rule: null, score: 0 }
  );

  if (matchedRule.rule) {
    return matchedRule.rule.reply;
  }

  return {
    text: "I do not have that answer yet. Try asking about services, mobile apps, web development, custom software, UX/UI, technologies, MVPs, pricing, contact details, or the company.",
  };
}

function getTopicKey(message) {
  const normalizedMessage = normalizeText(message);
  const messageTokens = new Set(normalizedMessage.split(" ").filter(Boolean));

  const topicMatchers = [
    { key: "contact", keywords: ["contact", "email", "phone", "reach"] },
    { key: "mobile", keywords: ["mobile", "android", "ios", "app"] },
    { key: "custom", keywords: ["custom", "software", "erp", "crm"] },
    { key: "website", keywords: ["website", "web", "frontend", "backend"] },
    { key: "uxui", keywords: ["ux", "ui", "design", "prototype", "branding"] },
    { key: "technologies", keywords: ["technology", "technologies", "stack", "react", "node", "python", "java", "aws", "azure"] },
    { key: "custom", keywords: ["maintenance", "support", "after delivery", "updates", "bug fix", "api"] },
    { key: "website", keywords: ["redesign", "improve website", "revamp", "upgrade website", "ecommerce", "e-commerce", "online store", "shopping website", "cloud", "deployment", "hosting", "server"] },
    { key: "uxui", keywords: ["audit", "ui audit", "ux audit", "design review"] },
    { key: "products", keywords: ["product", "products", "qshikshak", "school management", "school", "hr assist", "hr software", "payroll"] },
    { key: "about", keywords: ["industries", "domains", "sectors", "who do you work for", "why quinterra", "difference", "why choose you", "unique", "special"] },
    { key: "services", keywords: ["service", "services", "offer", "solutions"] },
    { key: "about", keywords: ["about", "company", "quinterra", "mission", "startup", "mvp"] },
  ];

  const matchedTopic = topicMatchers.find(({ keywords }) =>
    keywords.some((keyword) => getKeywordScore(normalizedMessage, messageTokens, keyword) > 0)
  );

  return matchedTopic ? matchedTopic.key : null;
}

export default function FloatingChatbot() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [messages, setMessages] = useState(INITIAL_MESSAGES);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [showLauncherHint, setShowLauncherHint] = useState(false);
  const panelRef = useRef(null);
  const latestMessageRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const quickActions = useMemo(() => QUICK_ACTIONS, []);

  const scrollLatestMessageIntoView = () => {
    const latestMessageElement = latestMessageRef.current;

    if (!latestMessageElement) {
      return;
    }

    latestMessageElement.scrollIntoView({
      behavior: "auto",
      block: "end",
    });
  };

  const handleSendMessage = (messageText) => {
    const trimmedMessage = messageText.trim();

    if (!trimmedMessage || isBotTyping) {
      return;
    }

    const reply = getReply(trimmedMessage);

    setMessages((currentMessages) => [
      ...currentMessages,
      { sender: "user", text: trimmedMessage },
    ]);
    setInputValue("");
    setIsBotTyping(true);

    typingTimeoutRef.current = window.setTimeout(() => {
      setMessages((currentMessages) => [
        ...currentMessages,
        { sender: "bot", text: reply.text, action: reply.action },
      ]);
      setIsBotTyping(false);
    }, 900);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleSendMessage(inputValue);
  };

  const showQuickActions = messages.length === INITIAL_MESSAGES.length;
  const askedQuestions = new Set(
    messages
      .filter((message) => message.sender === "user")
      .map((message) => normalizeText(message.text))
  );
  const lastUserMessage = [...messages]
    .reverse()
    .find((message) => message.sender === "user");
  const topicKey = lastUserMessage ? getTopicKey(lastUserMessage.text) : null;
  const relatedQuestions = topicKey ? RELATED_QUESTION_MAP[topicKey] || [] : [];
  const unusedRelatedQuestions = relatedQuestions.filter(
    (question) => !askedQuestions.has(normalizeText(question))
  );
  const unusedGeneralQuestions = FOLLOW_UP_QUESTIONS.filter(
    (question) =>
      !askedQuestions.has(normalizeText(question)) &&
      !unusedRelatedQuestions.includes(question)
  );
  const followUpQuestions = [
    ...unusedRelatedQuestions,
    ...unusedGeneralQuestions,
  ].slice(0, 4);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        window.clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    requestAnimationFrame(() => {
      scrollLatestMessageIntoView();
      window.setTimeout(scrollLatestMessageIntoView, 60);
    });
  }, [messages, isOpen, isBotTyping]);

  return (
    <div className="floatingChatbot">
      {isOpen ? (
        <div className="floatingChatbotPanel" ref={panelRef}>
          <div className="floatingChatbotHeader">
            <div>
              <h3>Quinterra Assistant</h3>
            </div>
            <button
              type="button"
              className="floatingChatbotClose"
              onClick={() => setIsOpen(false)}
              aria-label="Close chatbot"
            >
              x
            </button>
          </div>

          <div className="floatingChatbotContent">
            {showQuickActions ? (
              <div className="floatingChatbotQuickActions">
                {quickActions.map((action) => (
                  <button
                    key={action}
                    type="button"
                    className="floatingChatbotQuickButton"
                    onClick={() => handleSendMessage(action)}
                  >
                    {action}
                  </button>
                ))}
              </div>
            ) : null}

            <div className="floatingChatbotMessages">
              {messages.map((message, index) => (
                <div
                  key={`${message.sender}-${index}`}
                  ref={index === messages.length - 1 ? latestMessageRef : null}
                  className={`floatingChatbotMessage floatingChatbotMessage--${message.sender}`}
                >
                  <div className="floatingChatbotBubble">
                    <span className={message.isIntro ? "floatingChatbotIntroText" : ""}>
                      {message.text}
                    </span>
                    {message.action ? (
                      <button
                        type="button"
                        className="floatingChatbotActionLink"
                        onClick={() =>
                          navigate(message.action.route, {
                            state: message.action.state,
                          })
                        }
                      >
                        {message.action.label}
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}

              {isBotTyping ? (
                <div
                  ref={latestMessageRef}
                  className="floatingChatbotMessage floatingChatbotMessage--bot"
                >
                  <div className="floatingChatbotBubble">
                    <span className="floatingChatbotTyping">Typing...</span>
                  </div>
                </div>
              ) : null}
            </div>

            {!showQuickActions && !isBotTyping && followUpQuestions.length > 0 ? (
              <div className="floatingChatbotFollowUps">
                {followUpQuestions.map((question) => (
                  <button
                    key={question}
                    type="button"
                    className="floatingChatbotQuickButton"
                    onClick={() => handleSendMessage(question)}
                  >
                    {question}
                  </button>
                ))}
              </div>
            ) : null}

            <form className="floatingChatbotForm" onSubmit={handleSubmit}>
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder="Ask about the website..."
                aria-label="Ask Quinterra Assistant"
              />
              <button type="submit">Send</button>
            </form>
          </div>
        </div>
      ) : null}

      <button
        type="button"
        className="floatingChatbotLauncher"
        onClick={() => setIsOpen((currentState) => !currentState)}
        onMouseEnter={() => setShowLauncherHint(true)}
        onMouseLeave={() => setShowLauncherHint(false)}
        onFocus={() => setShowLauncherHint(true)}
        onBlur={() => setShowLauncherHint(false)}
        aria-label={isOpen ? "Hide chatbot" : "Open chatbot"}
      >
        <span className="floatingChatbotLauncherIcon" aria-hidden="true">
          <img src={require("../images/Chatbot.png")} alt="Chatbot" className="floatingChatbotLauncherImg" />
        </span>
      </button>
    </div>
  );
}
